/**
 * Voice Mate Web SDK Client
 * Main entry point for integrating Voice Mate voice AI platform
 */

import { EventEmitter } from './EventEmitter.js';
import { ConnectionManager } from './ConnectionManager.js';
import { SessionManager } from './SessionManager.js';
import { AudioProcessor } from '../audio/AudioProcessor.js';
import { PCMPlayer } from '../audio/PCMPlayer.js';
import { Logger } from '../utils/Logger.js';
import { Stats } from '../utils/Stats.js';
import { Validator } from '../utils/Validator.js';
import { VersionChecker } from '../utils/VersionChecker.js';
import { SessionError, AudioError } from '../errors/VoiceMateErrors.js';
import { VERSION } from '../version.js';

export class VoiceMateClient extends EventEmitter {
  /**
   * Create a Voice Mate client
   * @param {Object} config - Client configuration
   */
  constructor(config) {
    super();

    // Validate config
    Validator.validateConfig(config);

    this.config = this.mergeConfig(config);
    this.logger = new Logger(this.config.logLevel, '[VoiceMate]');
    this.versionChecker = new VersionChecker(this.logger);

    // Managers
    this.connectionManager = new ConnectionManager(this.config, this.logger);
    this.sessionManager = new SessionManager(this.config, this.logger);

    // Audio
    this.audioProcessor = null;
    this.pcmPlayer = null;

    // State
    this.state = {
      connection: 'disconnected',  // disconnected, connecting, connected
      call: 'idle',                // idle, starting, in_call, ending, ended
      muted: false,
      volume: this.config.audio.volume
    };

    // Stats
    this.stats = this.config.enableStats ? new Stats() : null;

    // Setup event forwarding
    this.setupEventForwarding();

    this.logger.info('VoiceMateClient initialized', {
      version: VERSION.sdk,
      serverUrl: this.config.serverUrl
    });
  }

  /**
   * Merge user config with defaults
   * @param {Object} config - User configuration
   * @returns {Object} Merged configuration
   */
  mergeConfig(config) {
    return {
      serverUrl: config.serverUrl,
      botNumber: config.botNumber,
      userNumber: config.userNumber || '',
      language: config.language || 'en',
      transport: config.transport || 'web',

      audio: {
        echoCancellation: config.audio?.echoCancellation ?? true,
        noiseSuppression: config.audio?.noiseSuppression ?? true,
        autoGainControl: config.audio?.autoGainControl ?? true,
        sampleRate: 16000,  // Fixed
        volume: config.audio?.volume ?? 1.0,
        ...config.audio
      },

      logLevel: config.logLevel || 'info',
      enableStats: config.enableStats ?? true,

      autoReconnect: config.autoReconnect ?? true,
      maxReconnectAttempts: config.maxReconnectAttempts || 5,
      reconnectDelay: config.reconnectDelay || 3000,

      customHeaders: config.customHeaders || {},

      sdkVersion: VERSION.sdk,
      minServerVersion: config.minServerVersion || VERSION.minServerVersion
    };
  }

  /**
   * Setup event forwarding from connection manager
   */
  setupEventForwarding() {
    // Forward connection events
    this.connectionManager.on('connecting', () => {
      this.state.connection = 'connecting';
      this.emit('connecting');
    });

    this.connectionManager.on('connected', () => {
      this.state.connection = 'connected';
      this.emit('connected', this.sessionManager.sessionId);
    });

    this.connectionManager.on('disconnected', (reason) => {
      this.state.connection = 'disconnected';
      this.emit('disconnected', reason);
    });

    this.connectionManager.on('reconnecting', (attempt) => {
      this.emit('reconnecting', attempt);
      if (this.stats) {
        this.stats.recordReconnect();
      }
    });

    this.connectionManager.on('reconnected', () => {
      this.emit('reconnected');
    });

    this.connectionManager.on('error', (error) => {
      this.emit('error', error);
      if (this.stats) {
        this.stats.recordError(error);
      }
    });

    // Handle messages
    this.connectionManager.on('message', (msg) => this.handleControlMessage(msg));
    this.connectionManager.on('audio:data', (data) => this.handleAudioData(data));
  }

  /**
   * Connect to server
   * @returns {Promise<void>}
   */
  async connect() {
    if (this.state.connection !== 'disconnected') {
      this.logger.warn('Already connected or connecting');
      return;
    }

    try {
      // Create session
      const sessionData = await this.sessionManager.createSession();

      // Check version compatibility
      if (sessionData.server_version) {
        await this.versionChecker.checkCompatibility(
          sessionData.server_version,
          this.config.sdkVersion,
          this.config.minServerVersion
        );
      }

      // Connect WebSocket
      await this.connectionManager.connect(sessionData.websocket_url);

      if (this.stats) {
        this.stats.startSession();
      }

      this.logger.info('Connected successfully');

    } catch (error) {
      this.logger.error('Connection failed:', error);
      throw error;
    }
  }

  /**
   * Disconnect from server
   * @returns {Promise<void>}
   */
  async disconnect() {
    this.logger.info('Disconnecting...');

    // Stop audio
    if (this.audioProcessor) {
      this.audioProcessor.stopCapture();
      this.audioProcessor = null;
    }
    if (this.pcmPlayer) {
      this.pcmPlayer.destroy();
      this.pcmPlayer = null;
    }

    // Close WebSocket
    this.connectionManager.disconnect();

    // Terminate session
    await this.sessionManager.terminateSession();

    this.state.connection = 'disconnected';
    this.state.call = 'idle';
  }

  /**
   * Start call
   * @returns {Promise<void>}
   */
  async startCall() {
    if (this.state.connection !== 'connected') {
      throw new SessionError('Not connected to server');
    }

    this.logger.info('Starting call...');
    this.state.call = 'starting';
    this.emit('call:starting');

    try {
      // Initialize audio
      await this.initializeAudio();

      this.state.call = 'in_call';
      this.emit('call:started');
      this.logger.info('Call started');

    } catch (error) {
      this.state.call = 'idle';
      this.logger.error('Failed to start call:', error);
      throw error;
    }
  }

  /**
   * End call
   * @returns {Promise<void>}
   */
  async endCall() {
    this.logger.info('Ending call...');
    this.state.call = 'ending';
    this.emit('call:ending');

    // Stop audio
    if (this.audioProcessor) {
      this.audioProcessor.stopCapture();
      this.audioProcessor = null;
    }

    if (this.pcmPlayer) {
      this.pcmPlayer.destroy();
      this.pcmPlayer = null;
    }

    this.state.call = 'ended';
    this.emit('call:ended', 'user_ended');

    setTimeout(() => {
      this.state.call = 'idle';
    }, 100);
  }

  /**
   * Initialize audio processing
   * @returns {Promise<void>}
   */
  async initializeAudio() {
    try {
      this.logger.info('Initializing audio...');

      // Initialize audio processor
      this.audioProcessor = new AudioProcessor({
        outputRate: this.config.audio.sampleRate,
        samplesPerFrame: 320,  // 20ms @ 16kHz
        bufferSize: 512,  // Low latency
        echoCancellation: this.config.audio.echoCancellation,
        noiseSuppression: this.config.audio.noiseSuppression,
        autoGainControl: this.config.audio.autoGainControl
      });

      // Set callback for outgoing audio
      this.audioProcessor.onAudioData = (floatData) => {
        if (!this.state.muted) {
          this.sendAudioFrame(floatData);
        }
      };

      // Optional: Monitor input level
      this.audioProcessor.onInputLevel = (rms) => {
        this.emit('audio:level', rms);
      };

      // Handle audio errors
      this.audioProcessor.onError = (error) => {
        this.emit('error', new AudioError('Audio processor error', {
          message: error.message
        }));
      };

      // Start capture
      await this.audioProcessor.startCapture();

      // Initialize PCM player
      this.pcmPlayer = new PCMPlayer({
        inputCodec: 'Int16',
        channels: 1,
        sampleRate: this.config.audio.sampleRate,
        flushTime: 40  // 40ms buffer
      });

      this.pcmPlayer.volume(this.state.volume);

      this.logger.info('Audio initialized successfully');

    } catch (error) {
      this.logger.error('Audio initialization failed:', error);
      throw new AudioError('Failed to initialize audio', {
        message: error.message,
        originalError: error
      });
    }
  }

  /**
   * Send audio frame
   * @param {Float32Array} floatData - Audio samples
   */
  sendAudioFrame(floatData) {
    // Convert float32 to int16
    const int16Data = new Int16Array(floatData.length);
    for (let i = 0; i < floatData.length; i++) {
      const s = Math.max(-1, Math.min(1, floatData[i]));
      int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    this.connectionManager.sendBinary(int16Data.buffer);

    if (this.stats) {
      this.stats.recordFrameOut(int16Data.byteLength);
    }
  }

  /**
   * Handle incoming audio data
   * @param {ArrayBuffer} arrayBuffer - Audio data
   */
  handleAudioData(arrayBuffer) {
    if (this.pcmPlayer) {
      this.pcmPlayer.feed(arrayBuffer);

      if (this.stats) {
        this.stats.recordFrameIn(arrayBuffer.byteLength);
      }
    }
  }

  /**
   * Handle control message from server
   * @param {Object} message - Control message
   */
  handleControlMessage(message) {
    this.logger.debug('Control message:', message);

    this.emit('control', message);

    switch (message.type) {
      case 'connected':
        // Server confirmation
        break;

      case 'speaking_started':
        this.emit('audio:speaking');
        break;

      case 'speaking_ended':
        this.emit('audio:listening');
        break;

      case 'interrupted':
        this.stopScheduledAudio();
        this.emit('audio:interrupted');
        break;

      default:
        // Custom control messages
        break;
    }
  }

  /**
   * Stop scheduled audio playback
   */
  stopScheduledAudio() {
    if (this.pcmPlayer) {
      this.pcmPlayer.samples = new Float32Array();
    }
  }

  /**
   * Mute microphone
   */
  mute() {
    this.state.muted = true;
    this.logger.info('Muted');

    if (this.audioProcessor) {
      this.audioProcessor.setMuted(true);
    }

    // Send interrupt to server
    this.sendControl({ type: 'interrupt', reason: 'user_mute' });
  }

  /**
   * Unmute microphone
   */
  unmute() {
    this.state.muted = false;
    this.logger.info('Unmuted');

    if (this.audioProcessor) {
      this.audioProcessor.setMuted(false);
    }
  }

  /**
   * Set playback volume
   * @param {number} volume - Volume level (0-2, where 1.0 = 100%)
   */
  setVolume(volume) {
    this.state.volume = Math.max(0, Math.min(2, volume));

    if (this.pcmPlayer) {
      this.pcmPlayer.volume(this.state.volume);
    }

    this.logger.info(`Volume set to ${this.state.volume}`);
  }

  /**
   * Send custom control message
   * @param {Object} message - Message to send
   */
  sendControl(message) {
    this.connectionManager.send(message);
  }

  /**
   * Get current state
   * @returns {Object} Current state
   */
  getState() {
    return {
      ...this.state,
      session: this.sessionManager.getSessionInfo()
    };
  }

  /**
   * Get statistics
   * @returns {Object|null} Statistics or null if disabled
   */
  getStats() {
    return this.stats ? this.stats.getSnapshot() : null;
  }
}
